import { IOnboarding, IUser, IUserInfo } from '@sdk/contracts';
import { DrawerProps, FormInstance, App } from 'antd';
import { useCallback, useRef, useState } from 'react';

import { StudentTrainingInfoFormRef } from '../../StudentTrainingInfoForm';

import {
  useMutateStudents,
  useMutateUserInfos,
  useMutateUserOnboarding,
  useUserOnboarding
} from '@/store';
import { useUserInfoByUser } from '@/store/users/user-info';

interface IUseEditStudentDrawerFormLogicParams {
  form: FormInstance<IUser>;
  data: IUser | undefined;
  drawerProps: DrawerProps | undefined;
  setData: (data?: IUser) => void;
  setIsOpen: (isOpen: boolean, props?: DrawerProps) => void;
}

export const useEditStudentDrawerFormLogic = ({
  form,
  data,
  drawerProps,
  setData,
  setIsOpen
}: IUseEditStudentDrawerFormLogicParams) => {
  const studentTrainingInfoRef = useRef<StudentTrainingInfoFormRef>(null);

  const studentId = data?._id || '';

  const { isWorking: isWorkingStudent, createStudent, updateStudent } = useMutateStudents();
  const { updateUserInfo, isWorking: isWorkingUserInfo } = useMutateUserInfos(studentId);
  const { userInfo } = useUserInfoByUser(studentId);
  const { userOnboarding: onboarding } = useUserOnboarding({ userId: studentId });
  const { updateUserOnboarding: updateOnboarding, isWorking: isWorkingOnboarding } =
    useMutateUserOnboarding();

  const [onboardingChanges, setOnboardingChanges] = useState<Partial<IOnboarding>>({});

  const isWorking = isWorkingStudent || isWorkingUserInfo || isWorkingOnboarding;

  const { message } = App.useApp();

  const handleOnboardingChange = useCallback((changes: Partial<IOnboarding>) => {
    setOnboardingChanges(prev => ({ ...prev, ...changes }));
  }, []);

  const closeDrawerDirectly = () => {
    setData();
    form.resetFields();
    setOnboardingChanges({});
    setIsOpen(false, drawerProps);
  };

  const saveUserInfoChanges = (userInfoData: Partial<IUserInfo>, onSuccess: () => void) => {
    updateUserInfo(userInfoData, {
      onSuccess: () => {
        onSuccess();
      },
      onError: (error: Error) => {
        message.error(error.message);
      }
    });
  };

  const saveOnboardingChanges = (onSuccess: () => void) => {
    if (Object.keys(onboardingChanges).length === 0 || !data?._id) {
      onSuccess();
      return;
    }

    updateOnboarding(
      { userId: data._id, update: onboardingChanges },
      {
        onSuccess: () => {
          onSuccess();
        },
        onError: (error: Error) => {
          message.error(error.message);
        }
      }
    );
  };

  const handleFinish = (values: Partial<IUser>) => {
    const trainingFormUserInfo = studentTrainingInfoRef.current?.getUserInfoValues();

    const mergedUserInfo = {
      ...(values.userInfo ?? {}),
      ...(trainingFormUserInfo ?? {})
    };

    const isEditing = !!data?._id;

    const action = isEditing ? updateStudent : createStudent;

    action(values, {
      onSuccess: () => {
        if (isEditing) {
          saveUserInfoChanges(mergedUserInfo, () => {
            saveOnboardingChanges(closeDrawerDirectly);
          });
        } else {
          closeDrawerDirectly();
        }
      },
      onError: (error: Error) => {
        message.error(error.message);
      }
    });
  };

  return {
    studentTrainingInfoRef,
    handleFinish,
    closeDrawerDirectly,
    isWorking,
    userInfo,
    onboarding,
    onboardingChanges,
    handleOnboardingChange
  };
};
